<template>
  <div class="ui-page">
    <app-filter-toolbar>
      <template #create>
        <app-button design="new" @click.native="showCreateModal">New leave request</app-button>
      </template>
      <template #primary>
        <app-input id="leave-request-search" v-model="searchFilter" design="new" type="search" placeholder="Name, ID or email" aria-label="Employee" @input.native="onRequestsSearch" />
      </template>
      <app-select id="leave-request-types" v-model="leaveTypeFilters" design="new" :options="leaveTypeOptions" label="name" placeholder="Leave type" aria-label="Leave type" multiple @input="onFiltersChange" />
      <app-date-range-picker
        :key="status"
        id="leave_request_dates"
        v-model="filterDates"
        :shortcut="null"
        :no-clear-button="status !== 'requested'"
        format="YYYY-MM-DD"
        formatted="ll"
        label="Requested dates"
        aria-label="Requested dates"
        overlay
        no-header
        no-label
        locale="en"
        range
        @is-hidden="onDatesHidden"
      />
      <template #selection>
        <app-filters v-model="status" design="new" refined weight="medium" :filters="statusOptions" name="leave-request-status" aria-label="Request status" @change="onStatusChange" />
      </template>
      <template v-if="hasFilters" #clear>
        <app-clear-filters-button design="new" icon-only @clear="clearFilters" />
      </template>
    </app-filter-toolbar>

    <app-content-skeleton v-if="!is_loaded || table_loading" label="Loading leave requests" :columns="tableColumns" min-width="1200px" />
    <app-error-state v-else-if="loadError" :message="loadError" @retry="fetchItems" />
    <template v-else>
      <div v-if="groups.length" class="ui-stack">
        <app-card
          v-for="group in groups"
          :key="group.type.code"
          design="new"
          variant="table"
          header-density="compact"
          :title="group.title" :count="group.requests_count"
        >
          <app-data-table design="new" density="dense" :columns="tableColumns" :rows="group.requests" :loading="table_loading" row-key="id" min-width="1200px">
            <template #cell-employee="{ row }">
              <app-table-text :text="row.employee.full_name">
                <router-link :to="{ name: 'employees-profile', params: { id: row.employee.id } }" class="ui-link">{{ row.employee.full_name }}</router-link>
              </app-table-text>
            </template>
            <template #cell-period="{ row }">
              <app-table-text :text="row.from_date || row.to_date ? `${row.kind === 'cancel_leave' ? 'Cancellation · ' : row.kind === 'change_dates' ? 'Date change · ' : ''}${formatDate(row.from_date)} – ${formatDate(row.to_date)}` : null" />
            </template>
            <template #cell-days="{ row }">{{ row.days }}</template>
            <template #cell-reason="{ row }">
              <app-table-text :text="row.reason" />
            </template>
            <template #cell-proof="{ row }">
              <a v-if="getProofUrl(row.proof)" :href="getProofUrl(row.proof)" class="ui-link" target="_blank" rel="noopener noreferrer">Open document</a>
              <app-status-badge v-else design="new" compact :label="getProofStateName(row)" :tone="proofTone(row)" />
            </template>
            <template #cell-submitted="{ row }">
              <app-table-text :text="formatRelativeDate(row.created_at)" :tooltip="formatDateTime(row.created_at)" class="ui-text" />
            </template>
            <template #cell-decision="{ row }">
              <span v-if="hasDecision(row)" v-tooltip="{ content: tooltipContent(row), placement: 'top-start', offset: 10, classes: 'tooltip--new' }" class="ui-link" tabindex="0">View decision</span>
              <span v-else>—</span>
            </template>
            <template #cell-actions="{ row }">
              <div v-if="row.status.code === 'requested'" class="ui-actions ui-actions--tight">
                <app-icon-button label="Preview in schedule" :disabled="actionLoadingId !== null" @click="$refs.calendarPreview.open(row)">
                  <i class="pi pi-calendar" aria-hidden="true" />
                </app-icon-button>
                <app-icon-button label="Approve" :disabled="actionLoadingId !== null" @click="showApproveModal(row)">
                  <i class="pi pi-check" aria-hidden="true" />
                </app-icon-button>
                <app-icon-button label="Reject" :disabled="actionLoadingId !== null" @click="showRejectModal(row)">
                  <i class="pi pi-times" aria-hidden="true" />
                </app-icon-button>
              </div>
              <span v-else>—</span>
            </template>
          </app-data-table>
        </app-card>
      </div>
      <app-no-results v-else design="new" title="No leave requests" text="" />

      <app-pagination
        v-if="groups.length"
        design="new"
        placement="embedded"
        :page="pagination.page"
        :pages="pagination.pages"
        :count="requestsCount"
        :visible-count="currentPageCount"
        :page-size="pageSize"
        :loading="table_loading"
        @change="onClickPagination"
      />
    </template>

    <leave-request-schedule-preview
      ref="calendarPreview"
      :sales="false"
      @approve="showApproveModal"
      @reject="showRejectModal"
    />

    <modal
      name="create-leave-request"
      :width="520"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :click-to-close="false"
      class="ui-modal modal-not-overflow"
      draggable=".app-modal-header__copy"
      :reset="true"
    >
      <section class="ui-modal__content" aria-labelledby="create-leave-request-title">
        <app-modal-header design="new" title="New leave request" title-id="create-leave-request-title" @close="closeCreateModal" />
        <form class="ui-modal__body" @submit.prevent="submitCreateForm">
          <app-form-group design="new" label="Employee" required>
            <app-select
              v-model="form.employee"
              design="new"
              :options="employeeOptions"
              :clearable="false"
              label="full_name"
              placeholder="Employee"
              :error="$v.form.employee.$error"
              @search="onEmployeeSearch"
            />
            <template #error>
              <div v-if="$v.form.employee.$dirty && !$v.form.employee.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group design="new" label="Leave type" required>
            <div
              v-if="isSalesLeave"
              class="sales-leave-type-switch"
              role="radiogroup"
              aria-label="Leave type"
            >
              <button
                v-for="option in leaveTypeOptions"
                :key="option.code"
                class="sales-leave-type-switch__option"
                :class="{ 'sales-leave-type-switch__option--active': form.type && form.type.code === option.code }"
                type="button"
                role="radio"
                :aria-checked="form.type && form.type.code === option.code ? 'true' : 'false'"
                @click="selectLeaveType(option)"
              >
                {{ option.name }}
              </button>
            </div>
            <app-select
              v-else
              v-model="form.type"
              design="new"
              :options="leaveTypeOptions"
              :clearable="false"
              placeholder="Leave type"
              :error="$v.form.type.$error"
              @input="onLeaveTypeChange"
            />
            <template #error>
              <div v-if="$v.form.type.$dirty && !$v.form.type.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group
            v-if="parentalDateRequired"
            design="new"
            :label="parentalDateLabel"
            required
          >
            <app-date-picker
              v-model="form.parental_date"
              design="new"
              :placeholder="parentalDateLabel"
              :disabled-date="isParentalDateDisabled"
              :error="$v.form.parental_date.$error"
              @input="onParentalDateChange"
            />
            <template #error>
              <div
                v-if="$v.form.parental_date.$dirty && !$v.form.parental_date.parentalDatePresent"
              >
                Required field
              </div>
              <div
                v-if="$v.form.parental_date.$dirty && !$v.form.parental_date.parentalDateInAllowedRange"
              >
                Birth Date cannot be later than 6 months from today
              </div>
            </template>
          </app-form-group>
          <app-form-group design="new" label="From date" required>
            <app-date-picker
              v-model="form.from_date"
              design="new"
              placeholder="From date"
              :readonly="parentalDateRequired && !form.parental_date"
              :disabled-date="isFromDateDisabled"
              :error="$v.form.from_date.$error"
            />
            <template #error>
              <div v-if="$v.form.from_date.$dirty && !$v.form.from_date.required">Required field</div>
              <div
                v-if="$v.form.from_date.$dirty && !$v.form.from_date.fromDateInAllowedRange"
              >
                {{ fromDateRangeError }}
              </div>
            </template>
          </app-form-group>
          <app-form-group design="new" label="To date" required>
            <app-date-picker
              v-model="form.to_date"
              design="new"
              placeholder="To date"
              :readonly="parentalDateRequired && !form.parental_date"
              :disabled-date="isToDateDisabled"
              :error="$v.form.to_date.$error"
            />
            <template #error>
              <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.required">Required field</div>
              <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.afterFromDate">
                To date cannot be earlier than from date
              </div>
              <div
                v-if="$v.form.to_date.$dirty && !$v.form.to_date.maximumLeaveDuration"
              >
                Leave cannot exceed {{ maximumLeaveDays }} days
              </div>
            </template>
          </app-form-group>
          <app-form-group design="new" label="Reason" :required="reasonRequired">
            <app-textarea
              v-model="form.reason"
              design="new"
              placeholder="Reason"
              :error="$v.form.reason.$error"
            />
            <template #error>
              <div v-if="$v.form.reason.$dirty && !$v.form.reason.reasonPresent">Required field</div>
            </template>
          </app-form-group>
          <app-form-group
            v-if="proofShownOnCreate"
            design="new"
            label="Supporting document"
            :required="proofRequiredOnSubmit"
          >
            <app-uploader
              v-model="form.proof"
              design="new"
              :extensions="proofExtensions"
              class="custom-uploader--flex"
              form-data-formats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
            />
            <template #error>
              <div v-if="$v.form.proof.$dirty && !$v.form.proof.proofPresent">
                Required field
              </div>
            </template>
          </app-form-group>
          <p v-if="createError" role="alert">{{ createError }}</p>
          <footer class="ui-modal__footer">
            <app-button design="new" theme="dark" type="button" :disabled="isCreating" @click.native="closeCreateModal">Cancel</app-button>
            <app-button ref="createSubmit" design="new" theme="success" type="submit" :disabled="isCreating">Create request</app-button>
            <app-button v-if="!isSalesLeave" design="new" theme="success" type="button" :disabled="isCreating" @click.native="submitCreateForm(true)">Create &amp; approve</app-button>
          </footer>
        </form>
      </section>
    </modal>

    <modal
      name="approve-leave-request"
      :width="440"
      :height="'auto'"
      :adaptive="true"
      :click-to-close="false"
      draggable=".app-modal-header__copy"
      class="ui-modal modal-not-overflow"
    >
      <section class="ui-modal__content" aria-labelledby="approve-leave-request-title">
        <app-modal-header design="new" title="Confirm approval" title-id="approve-leave-request-title" @close="closeActionModal" />
        <div class="ui-modal__body">
          <p class="ui-modal__message">Are you sure you want to approve this leave request?</p>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer class="ui-modal__footer">
            <app-button design="new" theme="dark" type="button" :disabled="actionLoadingId !== null" @click.native="closeActionModal">Cancel</app-button>
            <app-button ref="approveSubmit" design="new" theme="success" type="button" :disabled="actionLoadingId !== null" @click.native="approveRequest">Approve</app-button>
          </footer>
        </div>
      </section>
    </modal>

    <modal
      name="reject-leave-request"
      :width="440"
      :height="'auto'"
      :adaptive="true"
      :click-to-close="false"
      draggable=".app-modal-header__copy"
      class="ui-modal modal-not-overflow"
    >
      <section class="ui-modal__content" aria-labelledby="reject-leave-request-title">
        <app-modal-header design="new" title="Reject leave request" title-id="reject-leave-request-title" @close="closeActionModal" />
        <form class="ui-modal__body" @submit.prevent="rejectRequest">
          <app-form-group design="new" label="Rejection reason" required>
            <app-textarea
              v-model="rejectionReason"
              design="new"
              placeholder="Rejection reason"
              :error="$v.rejectionReason.$error"
            />
            <template #error>
              <div v-if="$v.rejectionReason.$dirty && !$v.rejectionReason.required">Required field</div>
            </template>
          </app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer class="ui-modal__footer">
            <app-button design="new" theme="dark" type="button" :disabled="actionLoadingId !== null" @click.native="closeActionModal">Cancel</app-button>
            <app-button ref="rejectSubmit" design="new" theme="error" type="submit" :disabled="actionLoadingId !== null">Reject</app-button>
          </footer>
        </form>
      </section>
    </modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { addDays, addMonths, addYears, differenceInCalendarDays, format, parseISO, subDays } from 'date-fns'
import { required } from 'vuelidate/lib/validators'
import LeaveRequestSchedulePreview from './LeaveRequestSchedulePreview'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import {
  geEmployeesLeaveTypes,
  getEmployeesLeaveRequestsGrouped,
  getEmployeesListSearch,
  getEmployeesSalesLeaveTypes,
  postEmployeesLeaveRequest,
  postEmployeesLeaveCreateAndApprove,
  postEmployeesLeaveRequestApprove,
  postEmployeesLeaveRequestReject,
  postEmployeesSalesLeaveRequest,
  postEmployeesSalesLeaveRequestApprove,
  postEmployeesSalesLeaveRequestReject,
} from '@/http'

const afterFromDate = (value, form) => !value || !form.from_date || value >= form.from_date
const LEAVE_DAYS_LIMITS = {
  paid_leave: 28,
  unpaid_leave: 28,
  sick_leave: 28,
  maternity_leave: 90,
  paternity_leave: 15,
}
const SALES_LEAVE_TYPE_ORDER = ['paid_leave', 'unpaid_leave', 'sick_leave']
const dateValue = date => format(date, 'yyyy-MM-dd')
const isParentalDateRequired = type => Boolean(type && type.parental_date_required)
const parentalDatePresent = (value, form) => !isParentalDateRequired(form.type) || Boolean(value)
const parentalDateInAllowedRange = (value, form) => {
  if (!value || !form.type || form.type.code !== 'paternity_leave') return true
  return value <= dateValue(addMonths(new Date(), 6))
}
const fromDateInAllowedRange = (value, form) => {
  if (!value || !form.type) return true
  const today = dateValue(new Date())
  if (form.is_sales_leave) return value >= today
  if (['paid_leave', 'unpaid_leave'].includes(form.type.code)) {
    return value >= today && value <= dateValue(addYears(new Date(), 1))
  }
  if (form.type.code === 'sick_leave') {
    return value >= dateValue(subDays(new Date(), 1)) && value <= today
  }
  if (form.type.code === 'paternity_leave' && form.parental_date) {
    return value >= dateValue(subDays(parseISO(form.parental_date), 3))
      && value <= dateValue(addMonths(parseISO(form.parental_date), 6))
  }
  return true
}
function maximumLeaveDuration(value, form) {
  if (!value || !form.from_date || !form.type) return true
  if (form.is_sales_leave) return true
  const maximumDays = this.maximumLeaveDays
  if (!maximumDays || value < form.from_date) return true
  return differenceInCalendarDays(parseISO(value), parseISO(form.from_date)) < maximumDays
}
const getProofRequirement = type => type && type.proof_requirement
const isProofRequiredOnSubmit = type => {
  const requirement = getProofRequirement(type)
  return Boolean(requirement && requirement.required_on_submit === true)
}
const isProofAcceptedOnCreate = type => {
  const requirement = getProofRequirement(type)
  return Boolean(requirement && requirement.accepted_on_create === true)
}
const proofPresent = (value, form) => !isProofRequiredOnSubmit(form.type) || Boolean(value && value.id)
const reasonPresent = (value, form) => {
  if (!form.is_sales_leave) return Boolean((value || '').trim())
  if (!form.type || !form.type.reason_required) return true
  return Boolean((value || '').trim())
}

export default {
  name: 'EmployeesLeavesRequests',
  components: { LeaveRequestSchedulePreview, AppContentSkeleton, AppErrorState, AppTableText },
  data() {
    return {
      status: 'requested',
      statusOptions: [
        { value: 'requested', label: 'Requested' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
      groups: [],
      requestsCount: 0,
      searchFilter: '',
      leaveTypeFilters: [],
      filterDates: null,
      pagination: {
        page: 1,
        pages: 1,
      },
      is_loaded: false,
      table_loading: false,
      loadError: '',
      fetchRequestId: 0,
      form: {
        is_sales_leave: false,
        employee: null,
        type: null,
        parental_date: null,
        from_date: null,
        to_date: null,
        reason: '',
        proof: null,
      },
      employeeOptions: [],
      leaveTypeOptions: [],
      proofExtensions: [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/zip',
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/tiff',
        'image/bmp',
      ],
      isCreating: false,
      createError: '',
      selectedRequest: null,
      actionLoadingId: null,
      actionError: '',
      rejectionReason: '',
    }
  },
  validations: {
    form: {
      employee: { required },
      type: { required },
      parental_date: { parentalDatePresent, parentalDateInAllowedRange },
      from_date: { required, fromDateInAllowedRange },
      to_date: { required, afterFromDate, maximumLeaveDuration },
      reason: { reasonPresent },
      proof: { proofPresent },
    },
    rejectionReason: { required },
  },
  computed: {
    isSalesLeave() {
      return this.$route.meta.leaveScope === 'sales'
    },
    tableColumns() {
      const columns = [
        { key: 'employee', label: 'Employee', width: '15%' },
        { key: 'period', label: 'Period', width: '19%' },
        { key: 'days', label: 'Days', width: '4%', align: 'right' },
        { key: 'reason', label: 'Reason', width: '16%' },
        { key: 'proof', label: 'Document', width: '10%' },
        { key: 'submitted', label: 'Requested at', width: '9%' },
        { key: 'decision', label: 'Decision', width: this.status === 'requested' ? '120px' : '27%' },
      ]
      if (this.status === 'requested') columns.push({ key: 'actions', label: 'Actions', width: '120px' })
      return columns
    },
    proofRequiredOnSubmit() {
      return isProofRequiredOnSubmit(this.form.type)
    },
    reasonRequired() {
      if (!this.isSalesLeave) return true
      return Boolean(this.form.type && this.form.type.reason_required)
    },
    proofShownOnCreate() {
      return this.proofRequiredOnSubmit || isProofAcceptedOnCreate(this.form.type)
    },
    parentalDateRequired() {
      return isParentalDateRequired(this.form.type)
    },
    parentalDateLabel() {
      return this.form.type && this.form.type.code === 'maternity_leave' ? 'Expected Birth Date' : 'Birth Date'
    },
    maximumLeaveDays() {
      const employee = this.form.employee
      if (!this.isSalesLeave && this.form.type && this.form.type.code === 'paid_leave'
        && employee && employee.position && employee.position.code === 'safari_driver') return null
      return this.form.type ? LEAVE_DAYS_LIMITS[this.form.type.code] : null
    },
    fromDateRangeError() {
      if (!this.form.type) return ''
      if (['paid_leave', 'unpaid_leave'].includes(this.form.type.code)) {
        return 'From date must be between today and one year from today'
      }
      if (this.form.type.code === 'sick_leave') {
        return 'From date must be yesterday or today'
      }
      if (this.form.type.code === 'paternity_leave') {
        return 'From date must be between 3 days before Birth Date and 6 months after Birth Date'
      }
      return ''
    },
    pageSize() {
      return this.status === 'requested' ? 40 : 20
    },
    currentPageCount() {
      return this.groups.reduce((count, group) => count + group.requests.length, 0)
    },
    hasFilters() {
      const dates = this.filterDates || {}
      const defaultDates = this.defaultDateRange() || {}
      return Boolean(
        this.searchFilter
        || this.leaveTypeFilters.length
        || dates.start !== defaultDates.start
        || dates.end !== defaultDates.end
      )
    },
  },
  created() {
    this.fetchItems()
    this.fetchLeaveTypes()
  },
  beforeDestroy() {
    this.fetchRequestId += 1
  },
  methods: {
    fetchItems() {
      const requestId = ++this.fetchRequestId
      this.table_loading = this.is_loaded
      this.loadError = ''
      const dateRange = this.getDateRangeParams()
      const request = getEmployeesLeaveRequestsGrouped
      return request({
        status: this.status,
        page: this.pagination.page,
        page_size: this.pageSize,
        search: this.searchFilter,
        type: this.leaveTypeFilters.map(type => type.code).join(','),
        ...dateRange,
      })
        .then(response => {
          if (requestId !== this.fetchRequestId) return
          this.groups = response.data.groups || []
          this.requestsCount = response.data.count
          this.pagination.pages = response.data.pages
          this.pagination.page = response.data.page
        })
        .catch(() => {
          if (requestId === this.fetchRequestId) this.loadError = 'Unable to load leave requests. Please try again.'
        })
        .finally(() => {
          if (requestId !== this.fetchRequestId) return
          this.is_loaded = true
          this.table_loading = false
        })
    },
    fetchLeaveTypes() {
      const request = this.isSalesLeave ? getEmployeesSalesLeaveTypes : geEmployeesLeaveTypes
      request().then(response => {
        const options = Array.isArray(response.data) ? response.data : []
        this.leaveTypeOptions = options
        if (this.isSalesLeave) {
          this.leaveTypeOptions = [...options].sort((left, right) => {
            return SALES_LEAVE_TYPE_ORDER.indexOf(left.code) - SALES_LEAVE_TYPE_ORDER.indexOf(right.code)
          })
        }
      })
    },
    onStatusChange(status) {
      this.status = status
      this.filterDates = this.defaultDateRange()
      this.pagination.page = 1
      this.fetchItems()
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onRequestsSearch: debounce(function () {
      this.onFiltersChange()
    }, 350),
    onFiltersChange() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onDatesHidden() {
      if (!this.filterDates && this.status !== 'requested') {
        this.filterDates = this.defaultDateRange()
      }
      if (!this.filterDates || (this.filterDates.start && this.filterDates.end)) {
        this.onFiltersChange()
      }
    },
    defaultDateRange() {
      if (this.status === 'requested') return null
      const today = new Date()
      return {
        start: format(subDays(today, 29), 'yyyy-MM-dd'),
        end: format(today, 'yyyy-MM-dd'),
      }
    },
    getDateRangeParams() {
      if (!this.filterDates) return {}
      return {
        created_from: this.filterDates.start,
        created_to: this.filterDates.end,
      }
    },
    clearFilters() {
      this.searchFilter = ''
      this.leaveTypeFilters = []
      this.filterDates = this.defaultDateRange()
      this.onFiltersChange()
    },
    onLeaveTypeChange() {
      this.form.parental_date = null
      this.form.from_date = null
      this.form.to_date = null
      this.form.proof = null
      this.$v.form.parental_date.$reset()
      this.$v.form.from_date.$reset()
      this.$v.form.to_date.$reset()
      this.$v.form.proof.$reset()
    },
    selectLeaveType(option) {
      this.form.type = option
      this.onLeaveTypeChange()
    },
    onParentalDateChange() {
      this.form.from_date = null
      this.form.to_date = null
      this.$v.form.from_date.$reset()
      this.$v.form.to_date.$reset()
    },
    isParentalDateDisabled(date) {
      if (!this.form.type || this.form.type.code !== 'paternity_leave') return false
      return dateValue(date) > dateValue(addMonths(new Date(), 6))
    },
    isFromDateDisabled(date) {
      if (!this.form.type) return false
      const value = dateValue(date)
      const today = dateValue(new Date())
      if (this.isSalesLeave) return value < today
      if (['paid_leave', 'unpaid_leave'].includes(this.form.type.code)) {
        return value < today || value > dateValue(addYears(new Date(), 1))
      }
      if (this.form.type.code === 'sick_leave') {
        return value < dateValue(subDays(new Date(), 1)) || value > today
      }
      if (this.form.type.code === 'paternity_leave' && this.form.parental_date) {
        return value < dateValue(subDays(parseISO(this.form.parental_date), 3))
          || value > dateValue(addMonths(parseISO(this.form.parental_date), 6))
      }
      return false
    },
    isToDateDisabled(date) {
      if (!this.form.from_date || !this.form.type) return false
      if (this.isSalesLeave) return dateValue(date) < this.form.from_date
      const maximumDays = this.maximumLeaveDays
      const value = dateValue(date)
      return value < this.form.from_date
        || Boolean(maximumDays && value > dateValue(addDays(parseISO(this.form.from_date), maximumDays - 1)))
    },
    showCreateModal() {
      this.createError = ''
      this.form = {
        is_sales_leave: this.isSalesLeave,
        employee: null,
        type: null,
        parental_date: null,
        from_date: null,
        to_date: null,
        reason: '',
        proof: null,
      }
      this.employeeOptions = []
      this.$v.form.$reset()
      this.$modal.show('create-leave-request')
    },
    closeCreateModal() {
      if (this.isCreating) return
      this.$modal.hide('create-leave-request')
    },
    submitCreateForm(approve = false) {
      this.$v.form.$touch()
      if (this.$v.form.$invalid || this.isCreating) return

      this.isCreating = true
      this.createError = ''
      this.$refs.createSubmit.preload = true
      let proof = null
      if (this.proofShownOnCreate && this.form.proof && this.form.proof.id) {
        proof = this.form.proof.id
      }
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequest : (approve === true ? postEmployeesLeaveCreateAndApprove : postEmployeesLeaveRequest)
      const payload = {
        employee: this.form.employee.id,
        type: this.form.type.code,
        from_date: this.form.from_date,
        to_date: this.form.to_date,
        reason: this.form.reason,
        proof,
      }
      if (!this.isSalesLeave) {
        payload.parental_date = this.parentalDateRequired ? this.form.parental_date : null
      }
      request(payload)
        .then(() => {
          this.status = 'requested'
          this.pagination.page = 1
          this.filterDates = null
          return this.fetchItems()
        })
        .then(() => {
          this.$modal.hide('create-leave-request')
          this.$notify({ type: 'success', title: 'Leave request created' })
        })
        .catch(error => { this.createError = (error.response && error.response.data && error.response.data.details) || 'Unable to create the request.' })
        .finally(() => {
          this.isCreating = false
          this.$refs.createSubmit.preload = false
        })
    },
    showApproveModal(row) {
      this.selectedRequest = row
      this.actionError = ''
      this.$modal.show('approve-leave-request')
    },
    showRejectModal(row) {
      this.selectedRequest = row
      this.rejectionReason = ''
      this.$v.rejectionReason.$reset()
      this.$modal.show('reject-leave-request')
    },
    closeActionModal() {
      if (this.actionLoadingId !== null) return
      this.$modal.hide('approve-leave-request')
      this.$modal.hide('reject-leave-request')
      this.selectedRequest = null
    },
    approveRequest() {
      if (!this.selectedRequest || this.actionLoadingId !== null) return
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequestApprove : postEmployeesLeaveRequestApprove
      this.sendAction(
        request(this.selectedRequest.id),
        'approveSubmit',
        'approve-leave-request',
        'Leave request approved'
      )
    },
    rejectRequest() {
      this.$v.rejectionReason.$touch()
      if (this.$v.rejectionReason.$invalid || !this.selectedRequest || this.actionLoadingId !== null) return
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequestReject : postEmployeesLeaveRequestReject
      this.sendAction(
        request(this.selectedRequest.id, { decision_reason: this.rejectionReason }),
        'rejectSubmit',
        'reject-leave-request',
        'Leave request rejected'
      )
    },
    sendAction(request, buttonRef, modalName, successTitle) {
      this.actionLoadingId = this.selectedRequest.id
      this.actionError = ''
      this.$refs[buttonRef].preload = true
      request
        .then(() => this.fetchItems())
        .then(() => {
          this.$modal.hide(modalName)
          this.selectedRequest = null
          this.$notify({ type: 'success', title: successTitle })
        })
        .catch(error => { this.actionError = (error.response && error.response.data && error.response.data.details) || 'Unable to process the request.' })
        .finally(() => {
          this.actionLoadingId = null
          this.$refs[buttonRef].preload = false
        })
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployees(loading, search)
    },
    searchEmployees: debounce(function (loading, search) {
      if (!search) {
        this.employeeOptions = []
        loading(false)
        return
      }
      getEmployeesListSearch({ search, resident: !this.isSalesLeave })
        .then(response => {
          this.employeeOptions = response.data
        })
        .finally(() => loading(false))
    }, 350),
    formatDate(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    formatDateTime(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    formatRelativeDate(date) {
      if (!date) return '-'
      const parsedDate = parseISO(date)
      if (Number.isNaN(parsedDate.getTime())) return '-'
      const seconds = Math.max(0, Math.floor((Date.now() - parsedDate.getTime()) / 1000))
      if (seconds < 60) return `${seconds}s`
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
      return `${Math.floor(seconds / 86400)}d`
    },
    getProofUrl(proof) {
      if (!proof) return ''
      if (typeof proof === 'string') return proof
      return proof.file || proof.url || ''
    },
    getProofState(row) {
      if (row.proof) return 'attached'
      const requirement = row.type && row.type.proof_requirement
      if (!requirement || requirement.code === 'not_required') return 'not-required'
      if (requirement.code === 'optional') return 'optional'
      if (['required_on_submit', 'required_later'].includes(requirement.code)) return 'missing'
      return 'not-required'
    },
    getProofStateName(row) {
      return {
        'attached': 'Attached',
        'missing': 'Missing',
        'optional': 'Optional',
        'not-required': 'Not required',
      }[this.getProofState(row)]
    },
    proofTone(row) {
      return {
        'attached': 'green',
        'missing': 'red',
        'optional': 'blue',
        'not-required': 'neutral',
      }[this.getProofState(row)]
    },
    hasDecision(row) {
      return Boolean(row.decision_reason || row.reviewed_by || row.reviewed_at)
    },
    getReviewerName(reviewer) {
      if (typeof reviewer === 'string') return reviewer
      return reviewer.full_name || reviewer.name || '-'
    },
    tooltipContent(row) {
      return `${row.decision_reason ? `${row.decision_reason}\n` : ''}${this.getReviewerName(row.reviewed_by)}\n${this.formatDateTime(row.reviewed_at)}`
    }
  }
}
</script>
